import type { IncomingHttpHeaders } from 'node:http';

const DEFAULT_TENANT = 'default';

type HeadersLike =
  | IncomingHttpHeaders
  | Headers
  | Record<string, string | string[] | undefined>;

function readHeader(headers: HeadersLike, name: string): string | undefined {
  if (typeof (headers as Headers).get === 'function') {
    const value = (headers as Headers).get(name);
    return value ?? undefined;
  }
  const raw = (headers as Record<string, string | string[] | undefined>)[name]
    ?? (headers as Record<string, string | string[] | undefined>)[name.toLowerCase()];
  if (Array.isArray(raw)) return raw[0];
  return raw;
}

/**
 * Server-side: resolve the tenant id from middleware-injected request headers.
 * Checks `x-tenant` first (next-external middleware), then `x-tenant-id`
 * (next-internal API convention). Returns undefined if neither is set so the
 * caller can decide whether to fall back to `'default'`.
 */
export function readTenantFromHeaders(headers: HeadersLike): string | undefined {
  return readHeader(headers, 'x-tenant') ?? readHeader(headers, 'x-tenant-id');
}

/**
 * Same as `readTenantFromHeaders` but falls back to `'default'`.
 */
export function readTenantFromHeadersOrDefault(headers: HeadersLike): string {
  return readTenantFromHeaders(headers) ?? DEFAULT_TENANT;
}

declare global {
  interface Window {
    __DECENT_TENANT__?: string;
  }
}

/**
 * Browser-side: returns the active tenant id (or undefined if not yet set).
 * Set via `setCurrentTenantClient` from `_app.tsx` once the tenant is known.
 */
export function getCurrentTenantClient(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.__DECENT_TENANT__ ?? readTenantFromCookie();
}

/**
 * Browser-side: reads the `x-tenant` cookie set by middleware. Available
 * synchronously at script load, before any React effect runs.
 */
export function readTenantFromCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(/(?:^|;\s*)x-tenant=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Browser-side: records the active tenant id for later reads
 * (e.g. by PostHog wiring). No-op on the server.
 */
export function setCurrentTenantClient(tenant: string): void {
  if (typeof window === 'undefined') return;
  window.__DECENT_TENANT__ = tenant;
}

export { DEFAULT_TENANT };
